import { useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { MdOutlineHistory } from "react-icons/md";
import { useDisclosure } from "@mantine/hooks";
import { ActionIcon, Drawer, LoadingOverlay, Stack, Title } from "@mantine/core";
import { IHistoryProvider } from "../HistoryProviders";
import { getToken, useLogin } from "../../authConfig";
import { useDomain } from "../../DomainContext";
import { groupHistory } from "./groupHistory";
import { useTranslation } from "react-i18next";
import { InfiniteLoadingButton } from "./InfiniteLoadingButton";
import { ChatHistoryItem, ChatHistoryItemDetails } from "../../api";
import { HistoryItem } from "./HistoryItem";

const HISTORY_COUNT_PER_LOAD = 20;

export interface ChatHistoryProps {
    historyManager: IHistoryProvider;
    notify: boolean;
    onChatHistoryItemClick: (historyItem: ChatHistoryItemDetails) => void;
}
export const ChatHistory = ({ historyManager, notify, onChatHistoryItemClick }: ChatHistoryProps) => {
    const { t } = useTranslation();
    const [historyOpened, { open: openHistory, close: closeHistory }] = useDisclosure(false);
    const [history, setHistory] = useState<ChatHistoryItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMoreHistory, setHasMoreHistory] = useState(false);

    const client = useLogin ? useMsal().instance : undefined;
    const { domain } = useDomain();

    useEffect(() => {
        if (!historyOpened) return;
        let isCancelled = false;

        const loadInitialHistory = async () => {
            setIsLoading(true);
            setHistory([]);
            historyManager.resetContinuationToken();
            setHasMoreHistory(true);

            try {
                const token = client ? await getToken(client) : undefined;
                const items = await historyManager.getNextItems(HISTORY_COUNT_PER_LOAD, token, domain);
                if (isCancelled) return;

                setHistory(items);
                if (items.length === 0) {
                    setHasMoreHistory(false);
                }
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        };

        void loadInitialHistory();

        return () => {
            isCancelled = true;
        };
    }, [historyOpened, historyManager, client, notify, domain]);

    const loadMoreHistory = async () => {
        setIsLoading(true);
        try {
            const token = client ? await getToken(client) : undefined;
            const items = await historyManager.getNextItems(HISTORY_COUNT_PER_LOAD, token, domain);
            if (items.length === 0) {
                setHasMoreHistory(false);
            }
            setHistory(prevHistory => [...prevHistory, ...items]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelect = async (id: string) => {
        setIsLoading(true);
        try {
            const token = client ? await getToken(client) : undefined;
            const item = await historyManager.getItem(id, token);

            if (item) {
                onChatHistoryItemClick(item);
                closeHistory();
            } else {
                console.error("Item not found:", id);
            }
        } catch (error) {
            setIsLoading(false);
            console.error("Failed to fetch history item:", error);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            const token = client ? await getToken(client) : undefined;
            await historyManager.deleteItem(id, token);
            setHistory(prevHistory => prevHistory.filter(item => item.id !== id));

            // Check if there is more history to load
            if (history.length <= HISTORY_COUNT_PER_LOAD) {
                setIsLoading(true);
                const items = await historyManager.getNextItems(HISTORY_COUNT_PER_LOAD, token, domain);
                if (items.length === 0) {
                    setHasMoreHistory(false);
                }
                setHistory(prevHistory => [...prevHistory, ...items]);
            }
        } catch (error) {
            console.error("Failed to delete history item:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const groupedHistory = useMemo(() => groupHistory(history), [history]);

    return (
        <>
            <ActionIcon variant="transparent" color="dark.4" onClick={openHistory}>
                <MdOutlineHistory size={24} />
            </ActionIcon>
            <Drawer withOverlay={true} position="right" offset={8} radius="md" opened={historyOpened} onClose={closeHistory}>
                <Stack gap="sm">
                    <LoadingOverlay visible={isLoading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
                    {Object.entries(groupedHistory).map(([group, items]) => (
                        <Stack gap="xs" key={group}>
                            <Title order={6}>{t(group)}</Title>
                            {items.map(item => (
                                <HistoryItem key={item.id} item={item} onSelect={handleSelect} onDelete={handleDelete} />
                            ))}
                        </Stack>
                    ))}
                </Stack>
                {history.length === 0 && !isLoading && <p>{t("history.noHistory")}</p>}
                {hasMoreHistory && !isLoading && <InfiniteLoadingButton func={loadMoreHistory} />}
            </Drawer>
        </>
    );
};
